/* eslint-disable @typescript-eslint/no-explicit-any */

const TOKEN_SCOPE = [
  "https://www.googleapis.com/auth/devstorage.read_only",
  "https://www.googleapis.com/auth/bigquery.readonly",
].join(" ");
const CLIENT_ID = import.meta.env.VITE_GCS_OAUTH_CLIENT_ID;

let tokenClient: any | null = null;
let accessToken: string | null = null;
let tokenExpiryEpochMs = 0;
let pendingTokenRequest: Promise<string> | null = null;

const TOKEN_STORAGE_KEY = "wpr_access_token";
const TOKEN_EXPIRY_STORAGE_KEY = "wpr_token_expiry";

function loadTokenFromStorage(): void {
  if (typeof window === "undefined") return;

  try {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedExpiry = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);

    if (storedToken && storedExpiry) {
      const expiryMs = parseInt(storedExpiry, 10);
      if (!isNaN(expiryMs) && Date.now() < expiryMs - 60_000) {
        accessToken = storedToken;
        tokenExpiryEpochMs = expiryMs;
      } else {
        // Token expired, clear storage
        clearTokenFromStorage();
      }
    }
  } catch (error) {
    console.warn("Failed to load token from localStorage:", error);
    clearTokenFromStorage();
  }
}

function saveTokenToStorage(token: string, expiryMs: number): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiryMs.toString());
  } catch (error) {
    console.warn("Failed to save token to localStorage:", error);
  }
}

function clearTokenFromStorage(): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear token from localStorage:", error);
  }
}

function ensureClientId(): void {
  if (!CLIENT_ID) {
    throw new Error("VITE_GCS_OAUTH_CLIENT_ID is not configured.");
  }
}

async function waitForGoogleScript(): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("Google Identity Services is only available in the browser environment.");
  }

  if ((window as any).google?.accounts?.oauth2) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const maxAttempts = 200;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if ((window as any).google?.accounts?.oauth2) {
        window.clearInterval(interval);
        resolve();
      } else if (attempts >= maxAttempts) {
        window.clearInterval(interval);
        reject(new Error("Timed out waiting for Google Identity Services script."));
      }
    }, 50);
  });
}

async function ensureTokenClient(): Promise<void> {
  if (tokenClient) {
    return;
  }

  ensureClientId();
  await waitForGoogleScript();

  tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: TOKEN_SCOPE,
    callback: () => {
      /* replaced when requestAccessToken runs */
    },
  });
}

function isTokenValid(): boolean {
  return Boolean(accessToken && Date.now() < tokenExpiryEpochMs - 60_000);
}

export function initializeAuth(): void {
  loadTokenFromStorage();
}

export function hasValidToken(): boolean {
  return isTokenValid();
}

export async function requestAccessToken(): Promise<string> {
  await ensureTokenClient();

  if (isTokenValid()) {
    return accessToken as string;
  }

  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  pendingTokenRequest = new Promise<string>((resolve, reject) => {
    tokenClient!.callback = (response: any) => {
      pendingTokenRequest = null;
      if (response.error) {
        reject(response);
        return;
      }
      accessToken = response.access_token;
      if (!accessToken) {
        reject(new Error("No access token received from Google"));
        return;
      }
      const expiresIn = Number(response.expires_in ?? 3600);
      tokenExpiryEpochMs = Date.now() + expiresIn * 1000;

      // Save token to localStorage
      saveTokenToStorage(accessToken, tokenExpiryEpochMs);

      resolve(accessToken);
    };

    tokenClient!.requestAccessToken({
      prompt: accessToken ? "" : "consent",
    });
  });

  return pendingTokenRequest;
}

export async function authenticatedFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = await requestAccessToken();
  const headers = new Headers(init?.headers ?? {});
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}

export function logout(): void {
  accessToken = null;
  tokenExpiryEpochMs = 0;
  pendingTokenRequest = null;
  clearTokenFromStorage();
}

export function oauthConfigAvailable(): boolean {
  return Boolean(CLIENT_ID);
}
